import { FC, useState, useEffect } from 'react';

import { useNavigate, Link } from 'react-router-dom';

import spot from 'data/spot.json';

import { useGetArticleData } from 'hooks/useGetArticleData';
import { useGetProgressData } from 'hooks/useGetProgressData';
import { useNews } from 'hooks/useNews';
import { usePageTitle } from 'hooks/usePageTitle';

import { CompletedArea } from 'components/features/CompletedArea';
import { Conversation } from 'components/features/Conversation';
import { Button } from 'components/parts/Button';
import { Paragraph } from 'components/parts/Paragraph';
import { SkipButton } from 'components/parts/SkipButton';
import { TOTAL_MISSIONS, SPECIAL_AREA_MISSION_IDS } from 'constants/mission';
import IconCard from 'images/icon/clear-card.svg';
import InfoAlert from 'images/icon/info_alert.svg';
import IconPrize from 'images/icon/prize_brick.svg';
import { SpotData, ProgressResponse } from 'types/spot';
import { isLatestNews } from 'utils/isLatestNews';

import styles from './Guide.module.scss';

type Props = {
  onNextPhase: () => void;
};

export const Guide: FC<Props> = ({ onNextPhase }) => {
  usePageTitle('チュートリアル');
  // Guide
  const [onboardingStep, setOnboardingStep] = useState(1);
  const handleClick = () => {
    if (onboardingStep < 2) {
      setOnboardingStep(onboardingStep + 1);
    } else {
      onNextPhase();
    }
  };
  const onSkipOnboarding = () => {
    setOnboardingStep(3);
    onNextPhase();
  };

  // Home
  const navigate = useNavigate();
  const { data: progressData } = useGetProgressData();
  const spotList: SpotData[] = spot as unknown as SpotData[];
  const recentlyExploredAreaNo = progressData?.recentlyExploredAreaNo;
  const recentlyExploredAreaArticle = useGetArticleData(String(recentlyExploredAreaNo) || '');
  const completedAreaList = progressData?.completedAreaList;
  const newsData = useNews(3);
  const [isLatestArray, setIsLatestArray] = useState<boolean[]>([]);

  function getCompletedArticles(
    spotList: SpotData[],
    completedAreaList: ProgressResponse['completedAreaList'] | undefined
  ): SpotData[] {
    if (completedAreaList === undefined) {
      return [];
    }

    return completedAreaList
      .map(completedId => spotList.find(spot => spot.id === completedId.toString()))
      .filter((article): article is SpotData => article !== undefined);
  }
  const completedArticles: SpotData[] = getCompletedArticles(spotList, completedAreaList);

  function getClearStampFileName(id: string) {
    return `${String(id).padStart(3, '0')}_clear-stamp.png`;
  }
  const is5Clear = completedAreaList?.length ? completedAreaList?.length >= 5 : false;
  const is10Clear = completedAreaList?.length ? completedAreaList?.length >= 10 : false;
  const isAllClear = progressData?.completedAreaList.length === 19;
  const receivedHalfway = progressData ? progressData?.rewardList.includes('halfway') : false;
  const receivedAlmost = progressData ? progressData?.rewardList.includes('almost') : false;
  const receivedPerfect = progressData ? progressData?.rewardList.includes('perfect') : false;

  useEffect(() => {
    if (newsData) {
      setIsLatestArray(newsData.map(news => isLatestNews(news.date)));
    }
  }, [newsData]);

  return (
    <>
      <div className={styles.wrapper} data-onboarding-step={onboardingStep}>
        <div className={styles.inner}>
          <header className={styles.header}>
            <h1 className={styles.logo}>
              <img
                className={styles.logoImage}
                src={require(`images/logo.webp`)}
                alt="コードF15 ODYSSEY 幸せの風吹く空への道しるべ"
              />
            </h1>
          </header>
          <div className={styles.body}>
            <div className={styles.lead}>
              <Paragraph addClass={[styles.leadText]}>
                魔法使いアマナ、精霊フシマといっしょに暗号を解読して「幸せの宝」を探そう。
              </Paragraph>
            </div>

            {recentlyExploredAreaArticle && (
              <div className={styles.recentlyExplored}>
                <Button
                  text="最近探索したエリアへ"
                  styleType="secondary"
                  handleClick={() => navigate(`/area/${recentlyExploredAreaArticle.id}`)}
                  addClass={[styles.spotButton]}
                />
              </div>
            )}

            <div className={styles.goArea}>
              <Button
                text="探しに行く！"
                styleType="primary"
                handleClick={() => navigate('/area/')}
                addClass={[styles.spotButton]}
              />
            </div>

            {/* クリアスタンプ */}
            <div className={styles.clearStamp}>
              <div className={styles.clearStampInner}>
                <h2 className={styles.clearStampHeading}>クリアスタンプ</h2>
                <ol className={[styles.clearStampList, styles.clearStampListFirst].join(' ')}>
                  {Array.from({ length: 5 }).map((_, i) => {
                    const index = i;
                    return (
                      <li key={index} className={styles.clearStampListItem}>
                        {completedArticles[index]?.id && (
                          <img
                            src={require(`components/pages/Area/images/${getClearStampFileName(
                              completedArticles[index].id
                            )}`)}
                            alt={`clear stamp of ${completedArticles[index].id}`}
                            className={styles.clearStampListImage}
                          />
                        )}
                      </li>
                    );
                  })}
                </ol>
                <div className={styles.clearStampButtonWrap}>
                  <button
                    onClick={() => navigate('/reward/halfway')}
                    disabled={receivedHalfway || !is5Clear}
                    className={styles.clearStampButton}
                  >
                    {!receivedHalfway && is5Clear && (
                      <img src={InfoAlert} alt="" className={styles.clearStampButtonIcon} />
                    )}
                    5エリアクリア賞
                  </button>
                </div>
                <ol
                  start={6}
                  className={[styles.clearStampList, styles.clearStampListSecond].join(' ')}
                >
                  {Array.from({ length: 5 }).map((_, i) => {
                    const index = i + 5;
                    return (
                      <li key={index} className={styles.clearStampListItem}>
                        {completedArticles[index]?.id && (
                          <img
                            src={require(`components/pages/Area/images/${getClearStampFileName(
                              completedArticles[index].id
                            )}`)}
                            alt={`clear stamp of ${completedArticles[index].id}`}
                            className={styles.clearStampListImage}
                          />
                        )}
                      </li>
                    );
                  })}
                </ol>
                <div className={styles.clearStampButtonWrap}>
                  <button
                    onClick={() => navigate('/reward/almost')}
                    disabled={receivedAlmost || !is10Clear}
                    className={styles.clearStampButton}
                  >
                    {!receivedAlmost && is10Clear && (
                      <img src={InfoAlert} alt="" className={styles.clearStampButtonIcon} />
                    )}
                    10エリアクリア賞
                  </button>
                </div>
                <ol
                  start={11}
                  className={[styles.clearStampList, styles.clearStampListSecond].join(' ')}
                >
                  {Array.from({ length: 9 }).map((_, i) => {
                    const index = i + 10;
                    return (
                      <li key={index} className={styles.clearStampListItem}>
                        {completedArticles[index]?.id && (
                          <img
                            src={require(`components/pages/Area/images/${getClearStampFileName(
                              completedArticles[index].id
                            )}`)}
                            alt={`clear stamp of ${completedArticles[index].id}`}
                            className={styles.clearStampListImage}
                          />
                        )}
                      </li>
                    );
                  })}
                </ol>
                <div className={styles.clearStampButtonWrap}>
                  <button
                    onClick={() => navigate('/reward/perfect')}
                    disabled={receivedPerfect || !isAllClear}
                    className={styles.clearStampButton}
                  >
                    {!receivedPerfect && isAllClear && (
                      <img src={InfoAlert} alt="" className={styles.clearStampButtonIcon} />
                    )}
                    パーフェクト賞
                  </button>
                </div>
              </div>
            </div>

            <div className={styles.nav}>
              <div className={styles.navItem}>
                <button onClick={() => navigate('/card')} className={styles.navButton}>
                  <img src={IconCard} alt="" className={styles.navIcon} />
                  <span className={styles.navText}>クリアカード</span>
                </button>
              </div>
            </div>
          </div>

          <div className={styles.award}>
            <button onClick={() => navigate('/prizes')} className={styles.navButton}>
              <img src={IconPrize} alt="" className={styles.navIcon} />
              <span className={styles.navText}>抽選応募賞品</span>
            </button>
            <p>解説文がここに入ります 解説文がここに入ります</p>
            <CompletedArea
              placeholder="プレゼント応募ランク"
              totalAreas={TOTAL_MISSIONS}
              completedAreas={0}
              isPrize={true}
            />
            <CompletedArea
              placeholder="浜通り賞"
              totalAreas={SPECIAL_AREA_MISSION_IDS.length}
              completedAreas={0}
              isPrize={false}
              withAreaUnit={true}
              altBarColor
            />
          </div>
          <div className={styles.footer}>
            <div className={styles.news}>
              <h2 className={styles.newsHeading}>お知らせ</h2>
              {newsData && (
                <ol className={styles.newsList}>
                  {newsData.map((news, index) => {
                    return (
                      <li key={index} className={styles.newsListItem}>
                        <Link to={`/news#${news.number}`} className={styles.newsListLink}>
                          <div className={styles.newsListHead}>
                            <p className={styles.newsListDate}>{news.date}</p>
                            {isLatestArray[index] && <p className={styles.newsListLabel}>NEW</p>}
                          </div>
                          <div className={styles.newsListBody}>
                            <p className={styles.newsListTitle}>{news.title}</p>
                          </div>
                        </Link>
                      </li>
                    );
                  })}
                </ol>
              )}
              <div className={styles.newsFoot}>
                <Button
                  text="お知らせ一覧"
                  styleType="secondary"
                  handleClick={() => navigate('/news/')}
                  addClass={[styles.spotButton]}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className={styles.guide} data-on-boarding={true}>
        <div className={styles.guideOverlay}></div>
        {onboardingStep === 1 && (
          <div className={styles.guideItem}>
            <div className={styles.guideSkip}>
              <SkipButton onClick={onSkipOnboarding} />
            </div>
            <button className={styles.guideButton} onClick={handleClick} disabled={false}>
              <Conversation role="chara-happy" showName={false} forceSmall={true}>
                遊び方ガイドは「メニュー」からいつでも確認できるよ。
              </Conversation>
            </button>
            <div className={styles.guideSpot1}></div>
          </div>
        )}
        {onboardingStep === 2 && (
          <div className={styles.guideItem}>
            <div className={styles.guideSkip}>
              <SkipButton onClick={onSkipOnboarding} />
            </div>
            <button className={styles.guideButton} onClick={handleClick} disabled={false}>
              <Conversation role="chara-normal" showName={false} forceSmall={true}>
                まずは行きたいエリアを選ぼう！
              </Conversation>
            </button>
            <div className={styles.guideSpot2}></div>
          </div>
        )}
      </div>
    </>
  );
};
